import React from 'react';
import { PrintButton } from '@/components/reports/print-button';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ShowGuideChip } from '@/components/ui/page-guide';
import { BeeFlight } from '@/components/brand/bee-flight';
import { GuideLink } from '@/components/ui/guide-link';
import { AskQbeeChip } from '@/components/qbee/qbee-buttons';
import { useQbeePageTitle } from '@/components/qbee/qbee-context';
import { Lede } from '@/components/ui/calm';
import { NAV_GROUPS, NAV_GROUPS_V2, flatItems } from '@/components/layout/sidebar';
import { useEmbedded } from '@/components/reports/embedded';
import { reportsV2Live } from '@/lib/reports-v2';

// Where a page sits in the app: its own icon and the sidebar section it lives
// in, read from the sidebar's one list. The longest path that matches wins, so
// a record page (/commerce/orders/123) wears its list's icon and section. The
// owner could not tell which report was where (3 Oct 2026); every page now
// says it at the top.
function identityFor(pathname) {
  let best = null;
  // The new reports name their own sections (Home, Performance, Store,
  // Tracking); a page reached through them wears that section.
  for (const g of (reportsV2Live() ? NAV_GROUPS_V2 : NAV_GROUPS) || []) {
    for (const it of flatItems(g.items || [])) {
      const to = String(it.to || '').split('?')[0];
      if (!to || !it.icon) continue;
      if ((pathname === to || pathname.startsWith(`${to}/`)) && (!best || to.length > best.len)) {
        best = { len: to.length, Icon: it.icon, group: g.label };
      }
    }
  }
  return best;
}

/**
 * Page header: title, optional description, optional right-side action slot,
 * optional `guideId` which renders a 'Show guide' chip beside the title when
 * the corresponding PageGuide has been dismissed.
 *
 * DESIGN.md section 4: main content padding 28px 32px 40px, the title at
 * 26px/600, the description quiet beneath it, actions on the right.
 */
// `art`: the ambient bees behind the header's right side. Left unset, a
// page gets them when a hash of its path lands in one of three buckets - so
// they turn up here and there, and the same page always decides the same
// way. true forces them on, false off.
const hashPath = (p) => { let h = 0; for (const ch of p) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return h; };
// `eyebrow` + `titleClassName`: a RECORD page's title is not a name, it is an
// identifier. "Order" at 28px in the display face beside a 13-digit number was
// two objects fighting; the kind belongs in an eyebrow, the way every section
// in this app already labels itself, and the identifier is then free to be set
// as data. Both are optional and change nothing for a page with a real name.
// `guide`: the knowledge-base link beside the title. Left unset, the page's
// route picks it from lib/guides (so every page in the sidebar gets one without
// passing anything); a string is a key in that map, or a full URL; false hides it.
export function PageHeader({ title, eyebrow, description, action, guideId, guide, className, descriptionClassName, titleClassName, art }) {
  const { pathname } = useLocation();
  const embedded = useEmbedded();
  // Never behind the header's own buttons: the comb drawn under "Export CSV"
  // or a segmented toggle read as a rendering fault, not as decoration.
  // Every report can be saved as a PDF, from the same place on each of them.
  // It lived on Attribution alone, so a client on any other report had no way
  // to hand it to someone (Wavex, 3 Oct 2026).
  const isReport = /^\/(commerce|growth|reports)(\/|$)/.test(pathname);
  const who = identityFor(pathname);
  // Qbee says which page a question was asked on.
  useQbeePageTitle(typeof title === 'string' ? title : null);
  const showArt = art === undefined ? !action && !isReport && hashPath(pathname) % 3 === 0 : !!art;
  // Inside an explorer the explorer owns the title; the page keeps only its
  // own controls, on the right, so nothing it offered is lost.
  if (embedded) {
    return action ? <div className="mb-4 flex flex-wrap items-center justify-end gap-2 [&>div]:flex-wrap">{action}</div> : null;
  }
  return (
    <div
      className={cn(
        'relative mx-auto flex w-full max-w-[1440px] flex-col gap-3 px-4 pb-4 pt-6 sm:flex-row sm:items-start sm:justify-between sm:px-8 sm:pb-5 sm:pt-7',
        className
      )}
    >
      {showArt && <BeeFlight className="right-4" />}
      <div className="relative flex min-w-0 items-start gap-4">
        {/* v3 (7 Oct 2026): no icon tile beside the title; the breadcrumb
            above it says where the page lives. */}
        <div className="min-w-0">
        {(eyebrow || who?.group) && <p className="mb-0.5 text-[13px] text-muted-foreground">{eyebrow || who.group}</p>}
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className={cn('truncate text-[22px] font-semibold tracking-[-0.015em] text-foreground sm:text-[24px]', titleClassName)}>{title}</h1>
          {guideId && <ShowGuideChip id={guideId} />}
          {guide !== false && (
            /^https?:/.test(String(guide || ''))
              ? <GuideLink href={guide} />
              : guide ? <GuideLink guide={guide} /> : <GuideLink path={pathname} />
          )}
          <AskQbeeChip />
        </div>
        {/* One sentence of intro at rest; anything longer waits behind (i).
            A string is cut by splitLede, JSX renders as written. */}
        {description && (
          <Lede text={description} className={cn('mt-1 max-w-[72ch] text-[14px] leading-relaxed text-muted-foreground', descriptionClassName)} />
        )}
        </div>
      </div>
      {(action || isReport) && <div className="relative flex min-w-0 max-w-full shrink-0 flex-wrap items-center gap-2 sm:pt-1 [&>div]:flex-wrap">{action}{isReport && <PrintButton />}</div>}
    </div>
  );
}

/**
 * Standard page content wrapper. Sits directly under the header; the header
 * owns the gap between the title and the first block.
 */
// `narrow`: a reading-width column (a to-do list, a form) that still starts on
// the header's left edge. Narrowing the wrapper itself centred it, so the
// content floated in the middle of the page under a title pinned to the left.
export function PageContent({ className, narrow, children, ...props }) {
  // Inside an explorer the frame already pads the page.
  const embedded = useEmbedded();
  if (embedded) {
    return <div className={cn('w-full', className, 'px-0 pt-0 sm:px-0 sm:pt-0')} {...props}>{narrow ? <div className="max-w-3xl">{children}</div> : children}</div>;
  }
  return (
    <div className={cn('mx-auto w-full max-w-[1440px] px-4 pb-8 pt-1 sm:px-8 sm:pb-10 sm:pt-2', className)} {...props}>
      {narrow ? <div className="max-w-3xl">{children}</div> : children}
    </div>
  );
}
